import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getSubadminPayoutDetails } from "../../ApiCalls/Revenue";

/* ================================
   SUBADMIN PAYOUT DETAILS PAGE
================================ */

const SubadminPayoutDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const fetchDetails = async () => {
    try {
      const res = await getSubadminPayoutDetails(id);
      setSummary(res.data.summary);
      setRecords(res.data.data || []);
    } catch (error) {
      console.error("Failed to fetch subadmin payout details", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-sm text-gray-500">
        Loading subadmin payout details...
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* ===== HEADER ===== */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-green-700">
          Subadmin Payout Details
        </h1>
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-gray-600 hover:text-green-700"
        >
          ← Back
        </button>
      </div>

      {/* ===== SUMMARY ===== */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <SummaryCard
            title="Lifetime Paid"
            value={`₹${summary.lifetime_paid_to_subadmin}`}
          />
          <SummaryCard
            title="Pending Service Fee"
            value={`₹${summary.total_pending_service_fee}`}
          />
          <SummaryCard
            title="Commission %"
            value={`${summary.commission_percentage}%`}
          />
          <SummaryCard
            title="Pending Commission"
            value={`₹${summary.pending_subadmin_commission}`}
          />
          <SummaryCard
            title="Pending Admin Share"
            value={`₹${summary.pending_admin_share}`}
          />
        </div>
      )}

      {/* ===== DETAILS TABLE ===== */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-green-50 text-left">
            <tr>
              <th className="p-3">Subadmin</th>
              <th className="p-3">Total Earned</th>
              <th className="p-3">Service Fee (Total)</th>
              <th className="p-3">Service Fee (Pending)</th>
              <th className="p-3">Pending Balance</th>
              <th className="p-3">Available Balance</th>
              <th className="p-3">COD Balance</th>
              <th className="p-3">COD Service Fee Due</th>
            </tr>
          </thead>

          <tbody>
            {records.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="p-3">
                  <p className="font-medium">{r.name}</p>
                  <p className="text-xs text-gray-500">{r.email}</p>
                </td>

                <td className="p-3">₹{r.total_earned}</td>
                <td className="p-3">₹{r.service_fee_total}</td>
                <td className="p-3 text-orange-600">
                  ₹{r.service_fee_pending}
                </td>
                <td className="p-3 text-orange-600">
                  ₹{r.pending_balance}
                </td>
                <td className="p-3 text-green-700 font-semibold">
                  ₹{r.available_balance}
                </td>
                <td className="p-3">₹{r.cod_balance}</td>
                <td className="p-3 text-red-600">
                  ₹{r.cod_service_fee_due}
                </td>
              </tr>
            ))}

            {records.length === 0 && (
              <tr>
                <td
                  colSpan="8"
                  className="p-4 text-center text-gray-500"
                >
                  No payout details available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SubadminPayoutDetails;

/* ================================
   SUMMARY CARD
================================ */

const SummaryCard = ({ title, value }) => (
  <div className="bg-white rounded-lg shadow p-4">
    <p className="text-sm text-gray-500">{title}</p>
    <p className="text-lg font-bold text-green-700">{value}</p>
  </div>
);
